import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import apiRequestHeaders from '../../common/apiRequestHeaders';
import { LOADING_STATUS } from '../../common/constants';
import { doSendFeedbackToExternalEmail } from '../../common/api/feedBackService';
import { fetchSettlementReport } from '../../common/api/statisticsService';
import { RootState } from '../../app/store';

export interface SettlementReportRequest {
  accommodationId?: string;
  ownerId: string;
  bookingsStartDate: Date;
  bookingsEndDate: Date;
  isOwnerUser: boolean;
  noBookingsAfterEndDate: boolean;
}

export interface SettlementReportItemLine {
  bookingNumber: number;
  bookingId: string;
  bookingCreatedAt: string; // ISO 8601 date string
  startDate: string; // ISO 8601 date string
  endDate: string; // ISO 8601 date string
  bookingType: number;
  accommodationId?: string;
  productName: string;
  productNumber: string;
  payerFirstName: string;
  payerLastName: string;
  payerEmail?: string;
  payerPhoneNumber?: string;
  payerNotes?: string;
  ownerPurchasePrice: number;
  ownerPurchasePriceVatP: number;
  ownerPurchasePriceVat: number;
  isMainProduct: boolean;
}

export interface SettlementReportItem {
  settlementReportlines: SettlementReportItemLine[];
  extraBookingSettlementReportItems: SettlementReportItem[] | null;
}

export interface SettlementReport {
  settlementReportItems: SettlementReportItem[];
}

export interface ReportsState {
  settlementReport: SettlementReport;
  settlementReportStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: ReportsState = {
  settlementReport: {
    settlementReportItems: [],
  },
  settlementReportStatus: LOADING_STATUS.IDLE,
};

export const getSettlementReport = createAsyncThunk(
  'reports/getSettlementReport',
  async (
    settlementReportRequest: SettlementReportRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchSettlementReport(settlementReportRequest);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getSettlementReport.pending, state => {
        state.settlementReportStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSettlementReport.fulfilled, (state, action) => {
        state.settlementReportStatus = LOADING_STATUS.IDLE;
        state.settlementReport = action.payload;
        // console.log(state.settlementReport);
      })
      .addCase(getSettlementReport.rejected, state => {
        state.settlementReportStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectSettlementReportLoadingStatus = (state: RootState) =>
  state.reports.settlementReportStatus;
export const selectSettlementReport = (state: RootState) =>
  state.reports.settlementReport;

export default reportsSlice.reducer;
